/**
 * Schema.org JSON-LD generator for the storefront.
 *
 * Renders a `LocalBusiness` (or subtype matching the tenant's industry)
 * with embedded `Service[]`, opening hours, and address. Google + Bing
 * + DuckDuckGo all parse this — free rich-snippets in search results,
 * including business info card, opening hours, service list with prices.
 *
 * Rendered client-side after data fetch. Modern Googlebot executes JS
 * and indexes the resulting structured data — SSR would be ideal but
 * the storefront currently fetches via API after mount; SSR would
 * require a bigger refactor and is queued separately.
 *
 * Schema reference: https://schema.org/LocalBusiness
 */

import type { Tenant, Service, BusinessHour } from '@/components/storefront/types';

const APP_URL = process.env.NEXT_PUBLIC_APP_URL || 'https://eazybooking.de';

/**
 * Map our internal `business_type` strings to the most-specific
 * Schema.org subtype that Google recognises. Falls back to plain
 * `LocalBusiness` when there's no industry match — that's still
 * valid and gets indexed, just without a category icon in results.
 *
 * Schema hierarchy: schema.org/LocalBusiness → e.g. HealthAndBeautyBusiness
 *   → HairSalon / BeautySalon / DaySpa / NailSalon / ...
 */
function resolveSchemaType(businessType: string | null | undefined): string {
  if (!businessType) return 'LocalBusiness';
  const lower = businessType.toLowerCase();
  if (lower.includes('friseur') || lower.includes('barbier') || lower.includes('hair')) {
    return 'HairSalon';
  }
  if (lower.includes('nagel') || lower.includes('nail')) return 'NailSalon';
  if (lower.includes('kosmetik') || lower.includes('beauty')) return 'BeautySalon';
  if (lower.includes('spa') || lower.includes('wellness')) return 'DaySpa';
  if (lower.includes('massage')) return 'HealthAndBeautyBusiness';
  if (lower.includes('fitness') || lower.includes('gym') || lower.includes('coach')) {
    return 'HealthClub';
  }
  if (lower.includes('arzt') || lower.includes('praxis') || lower.includes('doctor')) {
    return 'MedicalBusiness';
  }
  return 'LocalBusiness';
}

/**
 * Schema.org openingHours encoding: ISO day-of-week shorthand
 * (Mo Tu We Th Fr Sa Su). Our `business_hours.day_of_week` is
 * 0=Sun…6=Sat (German DIN). The mapping flips ordering.
 */
const DAY_SHORT = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'] as const;

interface OpeningHoursSpec {
  '@type': 'OpeningHoursSpecification';
  dayOfWeek: string;
  opens: string;
  closes: string;
}

interface OfferSchema {
  '@type': 'Offer';
  price?: number;
  priceCurrency: string;
  availability: 'https://schema.org/InStock';
}

interface ServiceSchema {
  '@type': 'Service';
  name: string;
  description?: string;
  serviceType?: string;
  offers?: OfferSchema;
}

interface PostalAddressSchema {
  '@type': 'PostalAddress';
  streetAddress?: string;
  postalCode?: string;
  addressLocality?: string;
  addressRegion?: string;
  addressCountry?: string;
}

interface LocalBusinessSchema {
  '@context': 'https://schema.org';
  '@type': string;
  '@id': string;
  name: string;
  url: string;
  description?: string;
  image?: string;
  telephone?: string;
  email?: string;
  priceRange?: string;
  address?: PostalAddressSchema;
  openingHoursSpecification?: OpeningHoursSpec[];
  hasOfferCatalog?: {
    '@type': 'OfferCatalog';
    name: string;
    itemListElement: ServiceSchema[];
  };
  sameAs?: string[];
}

interface BuildInput {
  tenant: Tenant;
  services: Service[];
  businessHours: BusinessHour[];
  /** The canonical storefront URL — slug-routing OR custom-domain. */
  canonicalUrl: string;
}

/**
 * Compose the LocalBusiness JSON-LD object. Fields are added
 * conditionally — missing data drops the field rather than emitting
 * empty strings (which Google flags as warnings in Search Console).
 */
export function buildLocalBusinessSchema(input: BuildInput): LocalBusinessSchema {
  const { tenant, services, businessHours, canonicalUrl } = input;
  const type = resolveSchemaType(tenant.business_type);

  const schema: LocalBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': type,
    '@id': `${canonicalUrl}#business`,
    name: tenant.name,
    url: canonicalUrl,
  };

  if (tenant.description) schema.description = tenant.description;
  if (tenant.logo_url) schema.image = tenant.logo_url;
  if (tenant.phone || tenant.contact_phone) {
    schema.telephone = tenant.phone || tenant.contact_phone || undefined;
  }
  if (tenant.email || tenant.contact_email) {
    schema.email = tenant.email || tenant.contact_email || undefined;
  }

  // Price range — Google uses this to render the "$$" / "€€" indicator
  // in results. Derive from cheapest + most expensive service price.
  const prices = services
    .map((s) => parseFloat(String(s.price)))
    .filter((p) => !Number.isNaN(p) && p > 0);
  if (prices.length > 0) {
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    schema.priceRange = max <= 50 ? '€' : max <= 150 ? '€€' : max <= 300 ? '€€€' : '€€€€';
    // Use the cheapest service for the "from" indicator.
    void min;
  }

  // Address — drop fields that are blank so Google doesn't flag empties.
  if (tenant.address || tenant.city || tenant.zip) {
    const addr: PostalAddressSchema = { '@type': 'PostalAddress' };
    if (tenant.address) addr.streetAddress = tenant.address;
    if (tenant.zip) addr.postalCode = tenant.zip;
    if (tenant.city) addr.addressLocality = tenant.city;
    if (tenant.state) addr.addressRegion = tenant.state;
    if (tenant.country) addr.addressCountry = tenant.country;
    schema.address = addr;
  }

  // Opening hours — only emit days that are actually open.
  const hoursSpec: OpeningHoursSpec[] = businessHours
    .filter((h) => h.is_open && h.open_time && h.close_time)
    .map((h) => ({
      '@type': 'OpeningHoursSpecification' as const,
      dayOfWeek: `https://schema.org/${
        ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][
          h.day_of_week
        ]
      }`,
      // Schema.org wants HH:MM, our DB stores HH:MM:SS — strip seconds.
      opens: String(h.open_time).slice(0, 5),
      closes: String(h.close_time).slice(0, 5),
    }));
  if (hoursSpec.length > 0) schema.openingHoursSpecification = hoursSpec;

  // Services — emitted as OfferCatalog so Google can show "Services:
  // Hair Cut, Color, …" in the business panel.
  const activeServices = services.filter(
    (s) => !s.name?.includes('🪄') // skip demo/sample services from seed
  );
  if (activeServices.length > 0) {
    schema.hasOfferCatalog = {
      '@type': 'OfferCatalog',
      name: 'Services',
      itemListElement: activeServices.map<ServiceSchema>((svc) => {
        const priceNum = parseFloat(String(svc.price));
        const service: ServiceSchema = {
          '@type': 'Service',
          name: svc.name,
        };
        if (svc.description) service.description = svc.description;
        if (svc.category) service.serviceType = svc.category;
        if (!Number.isNaN(priceNum) && priceNum > 0) {
          service.offers = {
            '@type': 'Offer',
            price: priceNum,
            priceCurrency: 'EUR',
            availability: 'https://schema.org/InStock',
          };
        }
        return service;
      }),
    };
  }

  // Social links → schema.org `sameAs` array. Helps Google associate
  // this storefront with the tenant's existing online presence.
  const social = [
    tenant.instagram_url,
    tenant.facebook_url,
    tenant.twitter_url,
    tenant.youtube_url,
    tenant.tiktok_url,
    tenant.linkedin_url,
  ].filter((u): u is string => typeof u === 'string' && u.length > 0);
  if (social.length > 0) schema.sameAs = social;

  return schema;
}

/**
 * Resolve the canonical URL for a tenant's storefront. Prefers the
 * verified custom domain if one exists, falls back to slug routing on
 * the main app origin. This drives both the `url` field in the schema
 * AND the canonical `<link rel="canonical">` rendering above.
 *
 * Production-only: in dev (`localhost`) the URL stays as origin/slug
 * so test storefronts don't accidentally point at production.
 */
export function resolveCanonicalUrl(
  tenantSlug: string | null,
  customDomain: string | null
): string {
  if (
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname.includes('.local'))
  ) {
    // Dev: keep current origin
    return tenantSlug ? `${window.location.origin}/${tenantSlug}` : window.location.origin;
  }
  if (customDomain) return `https://${customDomain}`;
  return tenantSlug ? `${APP_URL}/${tenantSlug}` : APP_URL;
}
